import { supabase } from './supabase'

// Free-tier monthly caps. Premium users bypass via isPremium short-circuits
// in checkLimit / incrementUsage. Counters reset on the first action in a
// new calendar month — see the isNewMonth branch below. Substitutions and
// saved-recipe management are deliberately NOT in this list: they're cheap
// and sticky, kept unlimited even on free.
export type LimitKey =
  | 'receipt_scans'
  | 'recipe_scans'
  | 'recipe_url_imports'
  | 'ai_suggestions'

export const FREE_LIMITS: Record<LimitKey, number> = {
  receipt_scans:      5,
  recipe_scans:       3,
  recipe_url_imports: 3,
  ai_suggestions:     5,
}

type CheckResult = {
  used:      number
  limit:     number
  remaining: number
  hitLimit:  boolean
}

/**
 * Read current usage for a limit key, resetting the counter if it's a new
 * calendar month relative to the stored reset_at date. Returns null when
 * the user is premium (no limit applies) or userId is missing — callers
 * should treat null as "no gate, proceed".
 *
 * IMPORTANT: this does NOT increment usage. Call incrementUsage() after
 * the gated action succeeds. Two-step pattern matches the existing receipt
 * scanner so users who cancel before the action runs don't burn a count.
 */
export async function checkLimit(
  userId: string | null,
  isPremium: boolean,
  key: LimitKey,
): Promise<CheckResult | null> {
  if (isPremium || !userId) return null

  const usedColumn  = `${key}_this_month`
  const resetColumn = `${key}_reset_at`

  const { data } = await supabase
    .from('users')
    .select(`${usedColumn}, ${resetColumn}`)
    .eq('id', userId)
    .single()

  if (!data) return null

  const row       = data as Record<string, any>
  const resetDate = row[resetColumn] ? new Date(row[resetColumn]) : null
  const now       = new Date()
  const isNewMonth = !resetDate
    || resetDate.getMonth()    !== now.getMonth()
    || resetDate.getFullYear() !== now.getFullYear()

  let used: number
  if (isNewMonth) {
    used = 0
    await supabase
      .from('users')
      .update({
        [usedColumn]:  0,
        [resetColumn]: now.toISOString().split('T')[0],
      })
      .eq('id', userId)
  } else {
    used = row[usedColumn] ?? 0
  }

  const limit     = FREE_LIMITS[key]
  const remaining = Math.max(0, limit - used)
  return { used, limit, remaining, hitLimit: used >= limit }
}

/**
 * Bump the counter for a limit key. Should only be called AFTER the gated
 * action succeeds — never before, never on cached / no-op paths. No-op for
 * premium users or missing userId.
 *
 * Read-modify-write rather than a SQL increment because the column name is
 * dynamic; a server-side rpc would need one function per column. The race
 * window is small (sub-second) and acceptable for usage counters where the
 * worst case is undercounting by 1 in a rapid double-fire.
 */
export async function incrementUsage(
  userId: string | null,
  isPremium: boolean,
  key: LimitKey,
): Promise<void> {
  if (isPremium || !userId) return

  const usedColumn = `${key}_this_month`

  const { data } = await supabase
    .from('users')
    .select(usedColumn)
    .eq('id', userId)
    .single()

  const current = ((data as Record<string, any>)?.[usedColumn] ?? 0) as number
  await supabase
    .from('users')
    .update({ [usedColumn]: current + 1 })
    .eq('id', userId)
}
